import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

/**
 * Excel 数据分析教程动画
 * 
 * 核心效果：
 * 1. Excel 表格网格展示
 * 2. 公式栏输入动画
 * 3. 单元格逐个高亮
 * 4. 计算结果动态显示
 * 5. 简易柱状图生成
 * 
 * 配色方案：
 * - 背景：深黑 #0F1419
 * - 主色：暖橙 #FF6B35
 * - 辅色：冷青 #4ECDC4
 */

export const ExcelDataAnalysis: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Excel 数据
  const tableData = [
    { product: '产品 A', q1: 120, q2: 150, q3: 180 },
    { product: '产品 B', q1: 90, q2: 110, q3: 130 },
    { product: '产品 C', q1: 200, q2: 220, q3: 250 },
  ];

  // 步骤时间轴（总 240 帧）
  const step1End = 60;   // 表格显示
  const step2End = 120;  // 公式输入
  const step3End = 180;  // 计算结果
  const step4End = 240;  // 图表生成

  // 标题动画
  const titleOpacity = interpolate(frame, [0, 30], [0, 1], { extrapolateRight: 'clamp' });
  const titleScale = spring({
    frame,
    from: 0.8,
    to: 1,
    fps,
    config: { damping: 12, stiffness: 200 },
  });

  // 步骤说明
  const steps = [
    { icon: '📊', text: '查看数据表格', delay: 0 },
    { icon: '📝', text: '输入 SUM 公式', delay: 60 },
    { icon: '✅', text: '计算总销售额', delay: 120 },
    { icon: '📈', text: '生成可视化图表', delay: 180 },
  ];

  // 当前高亮单元格
  const getHighlightCell = () => {
    if (frame < 30) return null;
    if (frame < step2End) {
      // 高亮数据单元格
      const cellIndex = Math.floor((frame - 30) / 10) % 9;
      return { type: 'data', index: cellIndex };
    }
    if (frame < step3End) {
      // 高亮结果单元格
      return { type: 'result' };
    }
    return null;
  };

  const highlightCell = getHighlightCell();

  // 公式输入动画
  const formulaText = '=SUM(B2:D4)';
  const formulaProgress = interpolate(frame, [60, 100], [0, formulaText.length], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });
  const displayedFormula = formulaText.substring(0, Math.floor(formulaProgress));

  // 计算结果动画
  const resultOpacity = interpolate(frame, [120, 150], [0, 1], { extrapolateRight: 'clamp' });
  const resultScale = spring({
    frame: frame - 120,
    from: 0.5,
    to: 1,
    fps,
    config: { damping: 15, stiffness: 150 },
  });

  // 柱状图动画
  const chartBars = [120, 150, 180, 90, 110, 130, 200, 220, 250];
  
  return (
    <AbsoluteFill style={{
      background: '#0F1419',
      fontFamily: 'Arial, sans-serif',
      color: '#FFFFFF',
      padding: 60,
    }}>
      {/* 标题 */}
      <div style={{
        position: 'absolute',
        top: 40,
        left: 0,
        right: 0,
        textAlign: 'center',
        opacity: titleOpacity,
        transform: `scale(${titleScale})`,
      }}>
        <h1 style={{
          fontSize: 80,
          fontWeight: 700,
          color: '#FF6B35',
          margin: 0,
        }}>
          Excel 数据分析教程
        </h1>
        <p style={{
          fontSize: 32,
          color: '#4ECDC4',
          marginTop: 10,
        }}>
          SUM 函数与图表生成
        </p>
      </div>

      {/* 左侧：Excel 表格区域 */}
      <div style={{
        position: 'absolute',
        top: 240,
        left: 60,
        right: '52%',
        bottom: 100,
        display: 'flex',
        flexDirection: 'column',
        gap: 20,
      }}>
        {/* 公式栏 */}
        <div style={{
          background: '#1A1F2E',
          borderRadius: 10,
          padding: '15px 25px',
          border: '2px solid #2A2F3E',
          opacity: interpolate(frame, [60, 80], [0, 1], { extrapolateRight: 'clamp' }),
        }}>
          <div style={{
            fontSize: 20,
            color: '#8B92A5',
            marginBottom: 8,
          }}>
            fx 公式栏
          </div>
          <div style={{
            fontSize: 28,
            color: '#4ECDC4',
            fontFamily: 'Consolas, monospace',
          }}>
            {displayedFormula || <span style={{ opacity: 0.3 }}>输入公式...</span>}
            {frame >= 60 && frame < 100 && <span style={{
              display: 'inline-block',
              width: 2,
              height: 28,
              background: '#4ECDC4',
              marginLeft: 2,
              animation: 'blink 1s infinite',
            }} />}
          </div>
        </div>

        {/* Excel 表格 */}
        <div style={{
          background: '#1A1F2E',
          borderRadius: 10,
          overflow: 'hidden',
          border: '2px solid #2A2F3E',
          flex: 1,
        }}>
          {/* 表头 */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '150px 100px 100px 100px 120px',
            background: '#2A2F3E',
            borderBottom: '2px solid #3A3F4E',
          }}>
            <div style={{ padding: '15px 20px', fontWeight: 700, color: '#FF6B35' }}>产品</div>
            <div style={{ padding: '15px 10px', fontWeight: 700, color: '#FF6B35' }}>Q1</div>
            <div style={{ padding: '15px 10px', fontWeight: 700, color: '#FF6B35' }}>Q2</div>
            <div style={{ padding: '15px 10px', fontWeight: 700, color: '#FF6B35' }}>Q3</div>
            <div style={{ padding: '15px 10px', fontWeight: 700, color: '#FF6B35' }}>总计</div>
          </div>

          {/* 数据行 */}
          {tableData.map((row, rowIndex) => (
            <div key={rowIndex} style={{
              display: 'grid',
              gridTemplateColumns: '150px 100px 100px 100px 120px',
              borderBottom: '1px solid #2A2F3E',
              opacity: interpolate(frame, [rowIndex * 10, rowIndex * 10 + 20], [0, 1], {
                extrapolateRight: 'clamp',
              }),
            }}>
              <div style={{
                padding: '12px 20px',
                background: rowIndex % 2 === 0 ? '#1A1F2E' : '#1F242E',
              }}>
                {row.product}
              </div>
              <div style={{
                padding: '12px 10px',
                background: highlightCell?.type === 'data' && 
                  highlightCell.index >= rowIndex * 3 && 
                  highlightCell.index < (rowIndex + 1) * 3
                  ? 'rgba(78, 205, 196, 0.2)'
                  : (rowIndex % 2 === 0 ? '#1A1F2E' : '#1F242E'),
                border: highlightCell?.type === 'data' && 
                  highlightCell.index >= rowIndex * 3 && 
                  highlightCell.index < (rowIndex + 1) * 3
                  ? '2px solid #4ECDC4'
                  : 'none',
              }}>
                {row.q1}
              </div>
              <div style={{
                padding: '12px 10px',
                background: highlightCell?.type === 'data' && 
                  highlightCell.index >= rowIndex * 3 && 
                  highlightCell.index < (rowIndex + 1) * 3
                  ? 'rgba(78, 205, 196, 0.2)'
                  : (rowIndex % 2 === 0 ? '#1A1F2E' : '#1F242E'),
              }}>
                {row.q2}
              </div>
              <div style={{
                padding: '12px 10px',
                background: highlightCell?.type === 'data' && 
                  highlightCell.index >= rowIndex * 3 && 
                  highlightCell.index < (rowIndex + 1) * 3
                  ? 'rgba(78, 205, 196, 0.2)'
                  : (rowIndex % 2 === 0 ? '#1A1F2E' : '#1F242E'),
              }}>
                {row.q3}
              </div>
              <div style={{
                padding: '12px 10px',
                background: highlightCell?.type === 'result' 
                  ? 'rgba(255, 107, 53, 0.2)'
                  : (rowIndex % 2 === 0 ? '#1A1F2E' : '#1F242E'),
                border: highlightCell?.type === 'result' ? '2px solid #FF6B35' : 'none',
                fontWeight: 700,
                color: '#FF6B35',
                opacity: resultOpacity,
                transform: `scale(${resultScale})`,
              }}>
                {row.q1 + row.q2 + row.q3}
              </div>
            </div>
          ))}

          {/* 总计行 */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '150px 100px 100px 100px 120px',
            background: '#2A2F3E',
            fontWeight: 700,
            opacity: resultOpacity,
          }}>
            <div style={{ padding: '12px 20px', color: '#FF6B35' }}>总计</div>
            <div style={{ padding: '12px 10px' }}>410</div>
            <div style={{ padding: '12px 10px' }}>480</div>
            <div style={{ padding: '12px 10px' }}>560</div>
            <div style={{
              padding: '12px 10px',
              background: 'rgba(255, 107, 53, 0.3)',
              color: '#FF6B35',
              fontSize: 32,
              transform: `scale(${resultScale})`,
            }}>
              1450
            </div>
          </div>
        </div>
      </div>

      {/* 右侧：步骤说明 + 图表 */}
      <div style={{
        position: 'absolute',
        top: 240,
        left: '52%',
        right: 60,
        bottom: 100,
        display: 'flex',
        flexDirection: 'column',
        gap: 30,
      }}>
        {/* 步骤说明 */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 15,
        }}>
          {steps.map((step, i) => {
            const stepOpacity = interpolate(
              frame,
              [step.delay, step.delay + 20],
              [0, 1],
              { extrapolateRight: 'clamp' }
            );
            const isActive = frame >= step.delay && frame < (steps[i + 1]?.delay || 240);

            return (
              <div key={i} style={{
                fontSize: 24,
                padding: '18px 25px',
                background: isActive ? 'rgba(255, 107, 53, 0.2)' : 'rgba(26, 31, 46, 0.5)',
                borderRadius: 12,
                border: isActive ? '2px solid #FF6B35' : '2px solid transparent',
                opacity: stepOpacity,
                display: 'flex',
                alignItems: 'center',
                gap: 15,
                transition: 'all 0.3s',
              }}>
                <span style={{ fontSize: 28 }}>{step.icon}</span>
                <span style={{ fontWeight: isActive ? 700 : 400 }}>{step.text}</span>
                {isActive && <span style={{
                  marginLeft: 'auto',
                  color: '#4ECDC4',
                }}>进行中...</span>}
              </div>
            );
          })}
        </div>

        {/* 柱状图 */}
        <div style={{
          flex: 1,
          background: '#1A1F2E',
          borderRadius: 15,
          padding: 30,
          border: '2px solid #2A2F3E',
          opacity: interpolate(frame, [180, 200], [0, 1], { extrapolateRight: 'clamp' }),
        }}>
          <div style={{
            fontSize: 28,
            fontWeight: 700,
            marginBottom: 20,
            color: '#4ECDC4',
          }}>
            📊 销售数据可视化
          </div>
          <div style={{
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'space-around',
            height: 200,
            gap: 10,
          }}>
            {chartBars.map((value, i) => {
              const barHeight = interpolate(
                frame,
                [180 + i * 5, 180 + i * 5 + 30],
                [0, value],
                { extrapolateRight: 'clamp' }
              );
              const colors = ['#FF6B35', '#4ECDC4', '#9B59B6', '#FF6B35', '#4ECDC4', '#9B59B6', '#FF6B35', '#4ECDC4', '#9B59B6'];
              
              return (
                <div key={i} style={{
                  width: 40,
                  height: barHeight,
                  background: colors[i],
                  borderRadius: '8px 8px 0 0',
                  boxShadow: `0 0 20px ${colors[i]}40`,
                }} />
              );
            })}
          </div>
          <div style={{
            display: 'flex',
            justifyContent: 'space-around',
            marginTop: 15,
            fontSize: 14,
            color: '#8B92A5',
          }}>
            {['A1', 'A2', 'A3', 'B1', 'B2', 'B3', 'C1', 'C2', 'C3'].map((label, i) => (
              <div key={i} style={{ width: 40, textAlign: 'center' }}>{label}</div>
            ))}
          </div>
        </div>
      </div>
    </AbsoluteFill>
  );
};
